import { Children, useMemo, useState } from "react";
import {
  Chip,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  styled,
  IconButton,
  MenuItem,
  Menu
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import T from "utils/constants/T";
import { get } from "lodash";
import moment from "moment";
import {
  blockEmployee,
  deleteEmployeeProfile,
  fetchRegisteredEmployees,
  unBlockEmployee,
} from "store/reducers/registeredEmployees";
import { dispatch } from "store";
import { useNavigate } from "react-router";
import { getUserByUserID } from "store/reducers/getEmployeeData";
import { useSelector } from "react-redux";
import { WEBHOOK_ENABLED_SYSTEMS } from "utils/constants/common";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderTop: "inherit",
  overflow: "hidden",
  padding: "8px 0px 8px 10px",
}));

interface RegisteredEmployeesBodyProps {
  allTableRows: any[];
  page: number;
  rowsPerPage: number;
  totalTableRowsCount: number;
  totalPageCount: number;
  opan: boolean;
  anchorEl: any;
  handleClick: any;
  handleClose: any;
  companyId: string;
  systemCode: string;
  isLoading?: boolean;
}

interface Status {
  status: string;
  type: string;
}
const RegisteredEmployeesBody = ({
  allTableRows = [],
  page,
  rowsPerPage,
  totalTableRowsCount,
  totalPageCount,
  opan,
  anchorEl,
  handleClick,
  handleClose,
  companyId,
  systemCode,
  isLoading = false,
}: RegisteredEmployeesBodyProps) => {
  const [selectedEmploiyeedId, setSelectedEmploiyeedId] = useState(null);
  const [selectedUserID, setSelectedUserID] = useState(null);
  const [selectedEmployeeRole, setSelectedEmployeeRole] = useState(null);
  const navigate = useNavigate();
  let { isManagerAdmin, isPayrollOfficer } = useSelector((state: any) => state.auth)
  const isReadOnlyRole = isManagerAdmin || isPayrollOfficer
  const getScheduleName = (data: any, systemCode: string) => {
    let scheduleName = { payPeriodName: "", payPeriodId: "" };
    const isWebhookEnabled = get(data, "companies[0].webhook_enabled", false);
    const isWebhookSystem = WEBHOOK_ENABLED_SYSTEMS.includes(systemCode);

    if (
      ["TIMESITE_PRO", T.DEPUTY.toUpperCase()].includes(systemCode) ||
      (isWebhookEnabled && isWebhookSystem)
    ) {
      scheduleName.payPeriodName = get(
        data,
        "pay_period_originations[0].name",
        ""
      );
      scheduleName.payPeriodId = get(
        data,
        "pay_period_originations[0]._id",
        ""
      );
    } else if (systemCode.toUpperCase() === "MYOBEXO") {
      scheduleName.payPeriodName = get(
        data,
        "pay_period_originations[0].name",
        ""
      );
      scheduleName.payPeriodId = get(
        data,
        "pay_period_originations[0]._id",
        ""
      );
    }
    else if (systemCode === T.KEYPAY.toUpperCase()) {
      scheduleName.payPeriodName = get(
        data,
        "keypay_pay_schedules[0].name",
        ""
      );
      scheduleName.payPeriodId = get(data, "keypay_pay_schedules[0]._id", "");
    } else {
      scheduleName.payPeriodName = get(data, "xero_pay_calendars[0].Name", "");
      scheduleName.payPeriodId = get(data, "xero_pay_calendars[0]._id", "");
    }

    return scheduleName;
  };
  const loggedInUser = useSelector((state: any) => state.auth);
  const loggedInEmployer = loggedInUser?.user?._id;
  const selectedEmployee = useMemo(
    () => allTableRows.find((r) => r._id === selectedEmploiyeedId),
    [allTableRows, selectedEmploiyeedId]
  );

  const checkSalaryWag = (salaryWag: any, systemCode: any) => {
    let employeeType;
    if (systemCode === "DEPUTY") {
      employeeType = "Timesheet";
    } else {
      if (salaryWag === 2) {
        employeeType = "Timesheet";
      } else if (salaryWag === 3) {
        employeeType = "Salary & TimeSheet";
      } else {
        employeeType = "Salary";
      }
    }
    return employeeType;
  };

  const getStatus = (statusType: any): any => {
    const status: Status = { status: "", type: "" };
    switch (statusType) {
      case 0:
        status.status = "Blocked";
        status.type = "error";
        break;
      case 1:
        status.status = "Active";
        status.type = "success";
        break;
      case 2:
        status.status = "Uninvite";
        status.type = "info";
        break;
      case 3:
        status.status = "Terminated";
        status.type = "error";
        break;

      default:
        status.status = "Unknown";
        status.type = "error";
        break;
    }

    return status;
  };

  const deleteUserProfile = async () => {
    const isConfirmed = confirm(
      "Are you sure you want to delete this user profile?"
    );
    if (isConfirmed) {
      handleClose();
      try {
        await dispatch(deleteEmployeeProfile(selectedEmploiyeedId));
        await dispatch(fetchRegisteredEmployees(page, rowsPerPage, companyId));
      } catch (error) {
        console.error("Error deleting employee profile:", error);
      }
    }
  };

  const handleBlockEmployee = async () => {
    handleClose();
    const isBlocked = get(selectedEmployee, "user[0].is_block", 0) === 1;
    if (isBlocked) {
      await dispatch(unBlockEmployee(selectedUserID));
      await dispatch(fetchRegisteredEmployees(page, rowsPerPage, companyId));
    }
    else {
      await dispatch(blockEmployee(selectedUserID));
      await dispatch(fetchRegisteredEmployees(page, rowsPerPage, companyId));
    }
  };

  const handleViewUserDetail = async () => {
    handleClose();
    await dispatch(getUserByUserID(selectedUserID));
    navigate(
      `/employees/employeeDetails/${selectedUserID}/personalProfile/view`
    );
  };

  return (
    <>
      <TableBody>
        {isLoading && allTableRows.length === 0 ? (
          <TableRow>
            <StyledTableCell colSpan={10} sx={{ border: "none" }}>
              <Typography variant="body1" textAlign="center">
                {T.LOADING}
              </Typography>
            </StyledTableCell>
          </TableRow>
        ) : (
          <>
          {allTableRows && allTableRows.length === 0 ? (
            <TableRow>
              <StyledTableCell colSpan={10} sx={{ border: "none" }}>
                <Typography variant="body1" textAlign="center">
                  {T.NO_RECORDS}
                </Typography>
              </StyledTableCell>
            </TableRow>
            ) : (
        Children.toArray(allTableRows.map((record, index) => {
          const scheduleName = getScheduleName(
            record,
            get(record, "companies[0].systems.code", "")
          );
          const registrationDate = get(record, "createdAt", "")
            ? get(record, "createdAt", "")
            : get(record, "created_date", "");
          const registrationDateText =
            moment(registrationDate).format("DD MMM yyyy h:mm A");
          let status = getStatus(parseInt(record.is_active));
          const businessUnitName = get(record, "business_units[0].name");
          return (
            <TableRow sx={{ background: "white" }} key={record?._id}>
              {!companyId && (
                <StyledTableCell>
                  {get(record, "companies[0].company_name", "")}
                </StyledTableCell>
              )}

              <StyledTableCell>{get(record, "fullname", "")}</StyledTableCell>
              <StyledTableCell>{get(record, "email", "")}</StyledTableCell>
              <StyledTableCell>
                {get(record, "system_employee_id", "")}
              </StyledTableCell>
              <StyledTableCell>
                {checkSalaryWag(
                  get(record, "salary_wag", ""),
                  get(record, "companies[0].systems.code", "")
                )}
              </StyledTableCell>
              <StyledTableCell>{scheduleName.payPeriodName}</StyledTableCell>
              {systemCode === T.ASTUTE.toUpperCase() && (
                <StyledTableCell>{businessUnitName}</StyledTableCell>
              )}
              <StyledTableCell>
                <Chip
                  color={status.type}
                  label={status.status}
                  size="small"
                  variant="light"
                />
              </StyledTableCell>
              <StyledTableCell>{registrationDateText}</StyledTableCell>
              <StyledTableCell>
                <IconButton
                  //  id="basic-button"
                  aria-controls={"basic-menu"}
                  aria-haspopup="true"
                  onClick={(e: any) => {
                    handleClick(e);
                    setSelectedEmploiyeedId(record?._id);
                    setSelectedUserID(record?.user[0]?._id);
                    setSelectedEmployeeRole(record?.role[0]?.name);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </StyledTableCell>
            </TableRow>
          );
              }))
          )}
          </>
        )}
      </TableBody>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        keepMounted
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem onClick={handleViewUserDetail}>View</MenuItem>
        <MenuItem onClick={handleBlockEmployee} disabled={isReadOnlyRole}>{get(selectedEmployee, "user[0].is_block", 0) === 0 ? "Block" : "Unblock"}</MenuItem>
        <MenuItem onClick={deleteUserProfile} disabled={isReadOnlyRole || (loggedInEmployer === selectedUserID) || (selectedEmployeeRole === T.EMPLOYER)}>Delete</MenuItem>
      </Menu>
    </>
  );
};

export default RegisteredEmployeesBody;
